import { fillTemplate, initFilterList } from "@scripts/filterList";
import type { ComponentInit } from "@scripts/registry";

import { FEATURED_ARTICLES_ALL } from "./FeaturedArticles.types";

const FILTERS = "[data-fa-featured-articles-filters]";
const TRACK = "[data-fa-featured-articles-track]";
const LIST = "[data-fa-featured-articles-list]";
const ITEM = "[data-fa-featured-articles-item]";
const STATUS = "[data-fa-featured-articles-status]";
const PREV = '[data-fa-featured-articles-arrow="prev"]';
const NEXT = '[data-fa-featured-articles-arrow="next"]';

const CATEGORY = "data-fa-article-tile-category";
const RESULTS = "data-fa-featured-articles-results";
const MAX_VISIBLE = "data-fa-featured-articles-max-visible";
const DISABLED = "aria-disabled";
const HIDDEN = "hidden";

const BLOCK = "fa-featured-articles";

/* Sub-pixel track widths mean scrollLeft never lands exactly on scrollWidth - clientWidth. */
const EDGE = 1;

export const initFeaturedArticles: ComponentInit = (root) => {
	const filters = root.querySelector<HTMLElement>(FILTERS);
	const track = root.querySelector<HTMLElement>(TRACK);
	const list = root.querySelector<HTMLElement>(LIST);

	if (!filters || !track || !list) return;

	const items = [...list.querySelectorAll<HTMLElement>(ITEM)];
	const status = root.querySelector<HTMLElement>(STATUS);
	const prev = root.querySelector<HTMLElement>(PREV);
	const next = root.querySelector<HTMLElement>(NEXT);

	const authoredDisabled = new Map(
		[prev, next]
			.filter((button): button is HTMLElement => button !== null)
			.map((button) => [button, button.getAttribute(DISABLED)]),
	);

	const listeners = new AbortController();
	const { signal } = listeners;
	const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");

	const parsedMax = Number.parseInt(root.getAttribute(MAX_VISIBLE) ?? "", 10);
	const max = Number.isFinite(parsedMax) ? parsedMax : undefined;

	const shown = () => items.filter((item) => !item.hasAttribute(HIDDEN));

	const step = () => {
		const item = shown()[0] ?? items[0];

		if (!item) return track.clientWidth;

		const gap = Number.parseFloat(getComputedStyle(list).columnGap);

		return item.offsetWidth + (Number.isFinite(gap) ? gap : 0);
	};

	const sync = () => {
		if (!prev || !next) return;

		const furthest = track.scrollWidth - track.clientWidth;

		prev.setAttribute(DISABLED, String(track.scrollLeft <= EDGE));
		next.setAttribute(DISABLED, String(track.scrollLeft >= furthest - EDGE));
	};

	const rewind = () => {
		track.scrollTo({ left: 0, behavior: "auto" });
		sync();
	};

	const filter = initFilterList(
		{
			root,
			filters,
			items,
			allValue: FEATURED_ARTICLES_ALL,
			categoryAttribute: CATEGORY,
			block: BLOCK,
			inertTarget: list,
			applyVisible: (matched) => {
				const kept = max === undefined ? matched : matched.slice(0, max);

				for (const item of items) item.toggleAttribute(HIDDEN, !kept.includes(item));

				return kept;
			},
			onApplied: (_matched, shownItems) => {
				const template = root.getAttribute(RESULTS);

				if (!status || !template) return;

				status.textContent = fillTemplate(template, {
					count: shownItems.length,
					total: items.length,
				});
			},
			/* Twice: the `hidden` writes relayout the track, and a snap container re-snaps after
			   that layout, leaving a reset that ran before it a few pixels in. */
			onReflow: rewind,
		},
		signal,
	);

	const move = (direction: 1 | -1) => (event: Event) => {
		const button = event.currentTarget;

		if (button instanceof HTMLElement && button.getAttribute(DISABLED) === "true") return;

		track.scrollBy({
			left: direction * step(),
			behavior: reduced.matches ? "auto" : "smooth",
		});
	};

	prev?.addEventListener("click", move(-1), { signal });
	next?.addEventListener("click", move(1), { signal });
	track.addEventListener("scroll", sync, { passive: true, signal });
	window.addEventListener("resize", sync, { signal });

	sync();

	return () => {
		listeners.abort();
		filter.restore();

		for (const [button, value] of authoredDisabled) {
			if (value === null) button.removeAttribute(DISABLED);
			else button.setAttribute(DISABLED, value);
		}

		if (status) status.textContent = "";
	};
};
